<template>
  <div ref="pdfContent" class="pdf-content">
    <div
      v-for="(message, index) in messages"
      :key="index"
      class="message-card py-1 px-05 mb-1 rounded-lg"
      :class="{
        'mt-1': index === 0,
        'page-break': (index + 1) % 4 === 0, // Aplica la clase después de cada 4 elementos
      }"
    >
      <!-- Información resumida de la transacción -->
      <div class="message-info">
        <div class="">
          <feather-icon
            v-if="message.status === 'success'"
            :icon="
              message.status === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'
            "
            size="20"
            class="mr-05"
            :class="
              message.status === 'success' ? 'text-success' : 'text-danger'
            "
          />

          <b-img
            class="client-program mr-05"
            :src="getProgramImageRoute(message.program_id)"
            v-b-tooltip.hover.top
            :title="message.program"
          />

          <span class="text"
            >{{ message.concept }} - {{ message.program }}
          </span>
        </div>
        <div>
          <span class="text">{{
            formatCurrency(message.transaction.settleAmount)
          }}</span>
        </div>
      </div>

      <div class="transaction-details mt-2">
        <div class="message-row">
          <span class="text text-left">{{ t.transactionId }}:</span>
          <span class="text text-right">{{ message.transaction.transId }}</span>
        </div>
        <div class="message-row">
          <span class="text text-left">{{ t.card }}:</span>
          <span class="text text-right">{{ message.card }}</span>
        </div>
        <div class="message-row">
          <span class="text text-left">{{ t.datetime }}:</span>
          <span class="text text-right">{{
            formatAmericanDateTime(message.transaction.submitTimeUTC)
          }}</span>
        </div>
        <div class="message-row">
          <span class="text text-left">{{ t.concept }}:</span>
          <span class="text text-right">{{ message.concept }}</span>
        </div>
        <div class="message-row">
          <span class="text text-left">{{ t.amount }}:</span>
          <span class="text text-right">{{
            formatCurrency(message.transaction.settleAmount)
          }}</span>
        </div>
        <div class="message-row">
          <span class="text text-left">{{ t.status }}:</span>
          <span class="text d-flex align-items-center text-right">
            <feather-icon
              v-if="message.status === 'success'"
              icon="CheckCircleIcon"
              size="20"
              class="text-success me-1"
            />
            <span v-if="message.status !== 'success'" class="text-danger">
              <template
                v-if="message.message === 'This transaction has been declined.'"
              >
                {{ t.transactionDeclined }}
              </template>
              <template v-else>
                {{ message.message }}
              </template>
            </span>
          </span>
        </div>
        <div
          v-if="
            getPaymentDetail(message.program_id).is_automatic_payment &&
            message.status === 'success'
          "
          class="message-row"
        >
          <span class="text text-left">{{ t.scheduledPaymentDate }}:</span>
          <span class="text text-right">
            {{
              getPaymentDetail(message.program_id).automatic_payment_date_text
            }}</span
          >
        </div>
        <div
          v-if="
            getPaymentDetail(message.program_id).is_automatic_payment &&
            message.status === 'success'
          "
          class="message-row"
        >
          <span class="text text-left">{{ t.monthlyPaymentAmount }}:</span>
          <span class="text text-right">
            {{
              formatCurrency(
                getPaymentDetail(message.program_id).automatic_payment_amount
              )
            }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";

export default {
  props: {
    messages: {
      type: Array,
    },
    paymmentLinks: {
      type: Array,
    },
    t: {
      type: Object,
    },
  },
  methods: {
    getPaymentDetail(programId) {
      if (!this.paymmentLinks) return {};
      return this.paymmentLinks.find((link) => link.program_id === programId);
    },
    formatCurrency(amount, symbolPosition = "right") {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      const formattedAmount = formatter.format(amount);

      // Detectar la posición del símbolo de moneda en el valor formateado
      const isSymbolFirst = formattedAmount.startsWith(
        formatter.format(0).replace(/\d/g, "")
      );

      if (symbolPosition === "left" && !isSymbolFirst) {
        // Mover el símbolo a la izquierda si no está al principio
        const [value, symbol] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${symbol} ${value}`;
      }

      if (symbolPosition === "right" && isSymbolFirst) {
        // Mover el símbolo a la derecha si está al principio
        const [symbol, value] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${value} ${symbol}`;
      }

      // Devolver el valor formateado directamente si ya está en la posición correcta
      return formattedAmount;
    },
    formatAmericanDateTime(utcDateTime) {
      // Crear un objeto Date a partir de la cadena UTC
      const date = new Date(utcDateTime);

      // Opciones de formato corto
      const options = {
        month: "2-digit", // Mes en dos dígitos (03)
        day: "2-digit", // Día en dos dígitos (18)
        year: "numeric", // Año completo (2025)
        hour: "numeric", // Hora (10)
        minute: "2-digit", // Minutos con dos dígitos (58)
        hour12: true, // Formato de 12 horas (AM/PM)
        timeZone: "America/Los_Angeles", // Zona horaria (PST)
      };

      // Convertir la fecha a formato corto y amigable
      return date.toLocaleString("en-US", options);
    },
    async generatePDF() {
      this.$nextTick(async () => {
        await new Promise((resolve) => setTimeout(resolve, 500)); // Esperar a que todo se renderice

        const pdfContent = this.$refs.pdfContent; // Tomar el contenido del PDF

        if (!pdfContent) {
          console.error("No se encontró el contenido del PDF");
          return;
        }

        await html2pdf().from(pdfContent).save("receipt.pdf");
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@media print {
  .pdf-content {
    display: block !important;
  }
}

.pdf-content {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .mr-05 {
    margin-right: 0.5rem;
  }

  .px-05 {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .message-card {
    color: #fff;
    border-radius: 10px;
    margin: 0rem 1rem;
    background-color: #f0f0f0;

    .message-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .transaction-details {
      margin-top: 10px;
      background: #f0f0f0;
      border-radius: 8px;

      .message-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 4px;
      }
    }

    .text {
      font-size: 0.9rem;
      margin-left: 8px;
      color: #333;
      font-weight: 500;
    }
  }

  .client-program {
    width: 25px !important;
  }

  .page-break {
    page-break-after: always; /* Forzar salto de página */
    break-after: page; /* Compatibilidad con navegadores modernos */
  }
}
</style>